import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  FlaskConical, Play, Copy, Loader2, ShieldCheck, ShieldAlert,
  MessageSquare, Inbox, Bot, AlertTriangle, Sparkles, CircleDot,
  CheckCircle2, XCircle,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";

/**
 * Brief 3.4.7 follow-up — Autonomy rehearsal sandbox.
 *
 * Super_admin-only read-only stress test of Nerea's autonomous-DM pipeline.
 * Posts to `/admin/ops/autonomy-rehearsal` and renders the response as a
 * stack of forensic cards: Classification, Brain Output, Autonomy Decision,
 * Would Send, Would Ops_DM, Overrides Applied.
 *
 * The endpoint never writes to live pipeline collections — only one
 * audit row per call. UI surfaces that contract in the header so the
 * super_admin knows nothing they do here can hit a merchant.
 */
export default function AdminAutonomyRehearsal() {
  const { user } = useAuth();
  const [params] = useSearchParams();

  const [merchants, setMerchants] = useState([]);
  const [merchantId, setMerchantId] = useState(params.get("merchant_id") || "");
  const [senderUserId, setSenderUserId] = useState("0");
  const [senderUsername, setSenderUsername] = useState("");
  const [chatType, setChatType] = useState("dm");
  const [messageText, setMessageText] = useState("");
  const [forceAutonomyMode, setForceAutonomyMode] = useState(""); // "", "supervised", "autonomous"
  const [forceClassifierOff, setForceClassifierOff] = useState(false);
  const [forceKillSwitchOff, setForceKillSwitchOff] = useState(false);
  const [forcePauseOff, setForcePauseOff] = useState(false);

  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  // Load merchant dropdown — active merchants only (approved/live).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/merchants");
        // GET /api/admin/merchants returns `{items: [...]}` — same
        // canonical shape AdminMerchants.jsx consumes. Fall through
        // to legacy keys defensively.
        const all = data?.items || data?.merchants || (Array.isArray(data) ? data : []);
        const active = (Array.isArray(all) ? all : []).filter((m) =>
          ["approved", "live"].includes(m.status),
        );
        setMerchants(active);
      } catch (e) {
        toast.error("Could not load merchant list.");
      }
    })();
  }, []);

  const requestBody = useMemo(() => {
    const body = {
      merchant_id: merchantId,
      sender_user_id: Number(senderUserId) || 0,
      sender_username: senderUsername || null,
      chat_type: chatType,
      message_text: messageText,
    };
    if (forceAutonomyMode) body.force_autonomy_mode = forceAutonomyMode;
    if (forceClassifierOff) body.force_classifier_off = true;
    if (forceKillSwitchOff) body.force_kill_switch_off = true;
    if (forcePauseOff) body.force_pause_off = true;
    return body;
  }, [
    merchantId, senderUserId, senderUsername, chatType, messageText,
    forceAutonomyMode, forceClassifierOff, forceKillSwitchOff, forcePauseOff,
  ]);

  const onRun = async () => {
    setError("");
    if (!merchantId) { toast.error("Pick a merchant."); return; }
    if (!messageText.trim()) { toast.error("Message text is required."); return; }
    setRunning(true);
    setResult(null);
    try {
      const { data } = await api.post("/admin/ops/autonomy-rehearsal", requestBody);
      setResult(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "object" ? JSON.stringify(detail) : (detail || "Rehearsal failed."));
    } finally {
      setRunning(false);
    }
  };

  const onCopyCurl = async () => {
    const base = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    const url = `${base}/api/admin/ops/autonomy-rehearsal`;
    const curl = `curl -X POST '${url}' \\\n  -H 'Content-Type: application/json' \\\n  -H 'Authorization: Bearer <YOUR_SESSION_TOKEN>' \\\n  -d '${JSON.stringify(requestBody)}'`;
    try {
      await navigator.clipboard.writeText(curl);
      toast.success("curl copied to clipboard.");
    } catch {
      toast.error("Clipboard blocked — see browser settings.");
    }
  };

  if (user?.role !== "super_admin") {
    return (
      <div className="text-rose-200 text-[13px]" data-testid="rehearsal-forbidden">
        Super-admin only.
      </div>
    );
  }

  return (
    <div data-testid="autonomy-rehearsal-page" className="max-w-6xl space-y-7 pb-16">
      <header>
        <div className="flex items-center gap-2.5">
          <FlaskConical className="h-4 w-4 text-cyan-300/80" />
          <h1 className="text-[18px] tracking-wide text-white">Autonomy rehearsal</h1>
        </div>
        <p className="mt-2 text-[12.5px] text-zinc-400 max-w-2xl">
          Read-only sandbox for Nerea's autonomous-DM pipeline. Drives the
          classifier, brain, and autonomy decider with your inputs and shows
          what would be sent and to whom — without touching pending actions,
          outbound queues, or merchant rows. One audit row written per run.
        </p>
      </header>

      {/* Form */}
      <section className="rounded-2xl border border-white/[0.08] bg-white/[0.02] p-5 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Merchant</label>
            <select
              data-testid="rehearsal-merchant-select"
              className="w-full rounded-md border border-white/[0.08] bg-black/30 px-3 py-2 text-[13px] text-white"
              value={merchantId}
              onChange={(e) => setMerchantId(e.target.value)}
            >
              <option value="">— Select active merchant —</option>
              {merchants.map((m) => (
                <option key={m.merchant_id} value={m.merchant_id}>
                  {m.ref || m.merchant_id} — {m.legal_name || m.step_data?.step1?.legal_name || "—"} ({m.status})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Chat type</label>
            <div className="inline-flex rounded-md border border-white/[0.08] overflow-hidden">
              {["dm", "group"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setChatType(t)}
                  data-testid={`rehearsal-chat-type-${t}`}
                  className={`px-3 py-2 text-[12px] ${chatType === t ? "bg-white/[0.08] text-white" : "text-zinc-400 hover:text-white"} ${t === "group" ? "border-l border-white/[0.08]" : ""}`}
                >
                  {t.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Sender user_id</label>
            <input
              type="number"
              data-testid="rehearsal-sender-id"
              className="w-full rounded-md border border-white/[0.08] bg-black/30 px-3 py-2 text-[13px] text-white"
              value={senderUserId}
              onChange={(e) => setSenderUserId(e.target.value)}
              placeholder="e.g. 123456789"
            />
          </div>
          <div>
            <label className="block text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Sender @username (optional)</label>
            <input
              type="text"
              data-testid="rehearsal-sender-username"
              className="w-full rounded-md border border-white/[0.08] bg-black/30 px-3 py-2 text-[13px] text-white"
              value={senderUsername}
              onChange={(e) => setSenderUsername(e.target.value)}
              placeholder="founder_x"
            />
          </div>
        </div>

        <div>
          <label className="block text-[10.5px] uppercase tracking-[0.22em] text-zinc-500 mb-1.5">Merchant message</label>
          <textarea
            data-testid="rehearsal-message-text"
            className="w-full rounded-md border border-white/[0.08] bg-black/30 px-3 py-2 text-[13px] text-white min-h-[100px]"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="What's the sandbox URL?"
          />
        </div>

        {/* Overrides */}
        <div className="rounded-xl border border-amber-400/15 bg-amber-400/[0.025] p-3">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-amber-200/90 mb-2 flex items-center gap-2">
            <AlertTriangle className="h-3 w-3" /> Rehearsal overrides
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-[12px]">
            <div>
              <label className="block text-[10.5px] text-zinc-400 mb-1">force_autonomy_mode</label>
              <select
                data-testid="rehearsal-force-mode"
                className="w-full rounded-md border border-white/[0.08] bg-black/30 px-2 py-1.5 text-[12px] text-white"
                value={forceAutonomyMode}
                onChange={(e) => setForceAutonomyMode(e.target.value)}
              >
                <option value="">— use merchant config —</option>
                <option value="supervised">supervised</option>
                <option value="autonomous">autonomous</option>
              </select>
            </div>
            <ToggleCheckbox
              testid="rehearsal-force-classifier-off"
              label="force_classifier_off"
              checked={forceClassifierOff}
              onChange={setForceClassifierOff}
            />
            <ToggleCheckbox
              testid="rehearsal-force-killswitch-off"
              label="force_kill_switch_off"
              checked={forceKillSwitchOff}
              onChange={setForceKillSwitchOff}
            />
            <ToggleCheckbox
              testid="rehearsal-force-pause-off"
              label="force_pause_off"
              checked={forcePauseOff}
              onChange={setForcePauseOff}
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onRun}
            disabled={running || !merchantId || !messageText.trim()}
            data-testid="rehearsal-run-button"
            className="inline-flex items-center gap-2 rounded-md border border-cyan-400/30 bg-cyan-400/[0.10] px-4 py-2 text-[12.5px] text-cyan-100 hover:bg-cyan-400/[0.18] disabled:opacity-40"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
            Run rehearsal
          </button>
          <button
            type="button"
            onClick={onCopyCurl}
            data-testid="rehearsal-copy-curl"
            className="inline-flex items-center gap-2 rounded-md border border-white/[0.10] px-4 py-2 text-[12.5px] text-zinc-300 hover:bg-white/[0.04]"
          >
            <Copy className="h-3.5 w-3.5" /> Copy as curl
          </button>
        </div>
      </section>

      {/* Error */}
      {error && (
        <div data-testid="rehearsal-error" className="rounded-xl border border-rose-400/20 bg-rose-400/[0.03] p-4 text-rose-200 text-[12.5px]">
          {error}
        </div>
      )}

      {/* Result cards */}
      {result && <ResultCards result={result} />}
    </div>
  );
}

function ToggleCheckbox({ testid, label, checked, onChange }) {
  return (
    <label className="flex items-center gap-2 text-zinc-300 cursor-pointer">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testid}
        className="rounded border-white/20 bg-black/30"
      />
      <span className="text-[12px] font-mono">{label}</span>
    </label>
  );
}

// ---------- Result cards ----------

function ResultCards({ result }) {
  return (
    <div data-testid="rehearsal-results" className="space-y-4">
      <OverridesCard overrides={result.rehearsal_overrides_applied} merchant={result.merchant} />
      <ClassificationCard cls={result.classification} vocality={result.vocality} />
      <BrainOutputCard brain={result.brain_output} deflection={result.deflection_text} />
      <AutonomyDecisionCard decision={result.autonomy_decision} />
      <WouldSendCard wouldSend={result.would_send} />
      <WouldOpsDmCard rows={result.would_ops_dm} am={result.account_manager} />
    </div>
  );
}

function Card({ icon: Icon, title, testid, children, accent }) {
  return (
    <section
      data-testid={testid}
      className={`rounded-2xl border ${accent || "border-white/[0.08]"} bg-white/[0.02] p-5`}
    >
      <header className="flex items-center gap-2.5 mb-3">
        <Icon className="h-3.5 w-3.5 text-zinc-400" />
        <h3 className="text-[13px] text-zinc-200">{title}</h3>
      </header>
      {children}
    </section>
  );
}

function OverridesCard({ overrides, merchant }) {
  return (
    <Card icon={Sparkles} title="Inputs · merchant snapshot" testid="rehearsal-card-overrides">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]">
        <KeyValue k="Merchant" v={`${merchant?.ref || ""} · ${merchant?.merchant_id}`} />
        <KeyValue k="Status" v={merchant?.status} />
        <KeyValue k="Configured mode" v={merchant?.configured_autonomy_mode} />
        <KeyValue k="Force supervised" v={merchant?.force_supervised ? "ON" : "off"} />
        <KeyValue k="Paused until" v={merchant?.paused_until || "—"} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="rehearsal-overrides-applied">
        {(overrides || []).length === 0 ? (
          <span className="text-[11.5px] text-zinc-500 italic">No overrides applied.</span>
        ) : (
          (overrides || []).map((o) => (
            <span key={o} className="inline-flex items-center gap-1 rounded-full border border-amber-400/30 bg-amber-400/[0.06] px-2 py-0.5 text-[10.5px] text-amber-200 font-mono">
              <AlertTriangle className="h-2.5 w-2.5" /> {o}
            </span>
          ))
        )}
      </div>
    </Card>
  );
}

function ClassificationCard({ cls, vocality }) {
  const conf = cls?.confidence;
  return (
    <Card icon={CircleDot} title="Classifier · vocality" testid="rehearsal-card-classification">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-[12.5px]">
        <KeyValue k="Category" v={<Mono>{cls?.category}</Mono>} />
        <KeyValue
          k="Confidence"
          v={
            <span className={typeof conf === "number" && conf >= 0.7 ? "text-emerald-200" : "text-amber-200"}>
              {typeof conf === "number" ? conf.toFixed(2) : conf}
            </span>
          }
        />
        <KeyValue k="In-scope" v={cls?.in_scope ? <CheckMark /> : <Cross />} />
        <KeyValue k="Force supervised" v={cls?.force_supervised ? <CheckMark tone="amber" /> : <Cross />} />
        <KeyValue k="Vocality reason" v={<Mono>{vocality?.reason}</Mono>} />
        <KeyValue
          k="Urgent signal"
          v={vocality?.urgent_signal ? (
            <span className="inline-flex items-center gap-1 text-rose-200">
              <AlertTriangle className="h-3 w-3" /> {vocality.urgent_keywords?.join(", ") || "yes"}
            </span>
          ) : (
            <span className="text-zinc-500">no</span>
          )}
        />
      </div>
      {cls?.reasoning && (
        <div className="mt-3 rounded-md border border-white/[0.05] bg-black/30 p-2.5 text-[11.5px] text-zinc-400 italic">
          {cls.reasoning}
        </div>
      )}
    </Card>
  );
}

function BrainOutputCard({ brain, deflection }) {
  if (!brain && !deflection) {
    return (
      <Card icon={Bot} title="Brain output" testid="rehearsal-card-brain">
        <div className="text-[12px] text-zinc-500 italic">No brain call (out-of-scope deflection path).</div>
      </Card>
    );
  }
  if (deflection && !brain) {
    return (
      <Card icon={Bot} title="Brain output" testid="rehearsal-card-brain">
        <div className="text-[11.5px] text-zinc-400">Out-of-scope deflection — brain not called.</div>
        <div className="mt-2 rounded-md border border-amber-400/15 bg-amber-400/[0.04] p-2.5 text-[12.5px] text-amber-100">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-amber-200/80 mb-1">Locked deflection text</div>
          {deflection}
        </div>
      </Card>
    );
  }
  return (
    <Card icon={Bot} title="Brain output" testid="rehearsal-card-brain">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-[12.5px]">
        <KeyValue k="Confidence" v={<Mono>{brain.confidence_qual}</Mono>} />
        <KeyValue k="Abstained" v={brain.abstained ? <CheckMark tone="amber" /> : <Cross />} />
        <KeyValue k="Redacted" v={brain.redacted ? <CheckMark tone="rose" /> : <Cross />} />
      </div>
      <div className="mt-3 rounded-md border border-white/[0.06] bg-black/30 p-3 text-[12.5px] text-zinc-100 whitespace-pre-wrap break-words">
        {brain.content || <span className="text-zinc-500 italic">(empty)</span>}
      </div>
      {(brain.filter_reasons || []).length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <span className="text-[10.5px] text-zinc-500">filter reasons:</span>
          {brain.filter_reasons.map((r) => (
            <span key={r} className="inline-flex rounded border border-rose-400/25 bg-rose-400/[0.06] px-1.5 py-0.5 text-[10.5px] text-rose-200">
              {r}
            </span>
          ))}
        </div>
      )}
      {(brain.citations || []).length > 0 && (
        <div className="mt-3 text-[11.5px] text-zinc-500">
          <span className="text-zinc-400 mr-1">citations:</span>
          {brain.citations.map((c) => (
            <span key={c.chunk_id} className="mr-2">
              {c.source}/{c.source_title}
            </span>
          ))}
        </div>
      )}
    </Card>
  );
}

function AutonomyDecisionCard({ decision }) {
  const isAuto = decision?.final_mode === "autonomous";
  return (
    <Card
      icon={isAuto ? ShieldCheck : ShieldAlert}
      title="Autonomy decision"
      testid="rehearsal-card-autonomy"
      accent={isAuto ? "border-emerald-400/25" : "border-amber-400/25"}
    >
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <span className="text-zinc-400">Final mode:</span>
        <span
          data-testid="rehearsal-final-mode"
          className={
            isAuto
              ? "inline-flex items-center rounded-full border border-emerald-400/30 bg-emerald-400/[0.08] px-2.5 py-0.5 text-emerald-100"
              : "inline-flex items-center rounded-full border border-amber-400/30 bg-amber-400/[0.08] px-2.5 py-0.5 text-amber-100"
          }
        >
          {decision?.final_mode}
        </span>
        <span className="text-zinc-500 text-[11.5px]">
          (configured: {decision?.configured_mode})
        </span>
      </div>
      <div className="mt-3" data-testid="rehearsal-bypasses">
        {(decision?.bypasses_fired || []).length === 0 ? (
          <span className="text-[11.5px] text-zinc-500 italic">No bypasses fired.</span>
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[10.5px] text-zinc-500 mr-1">bypasses fired:</span>
            {decision.bypasses_fired.map((b) => (
              <span key={b} className="inline-flex items-center rounded-full border border-rose-400/25 bg-rose-400/[0.06] px-2 py-0.5 text-[10.5px] text-rose-200 font-mono">
                {b}
              </span>
            ))}
          </div>
        )}
      </div>
      {decision?.checks && (
        <details className="mt-3">
          <summary className="text-[11px] text-zinc-500 cursor-pointer hover:text-white">show checks</summary>
          <pre className="mt-2 rounded-md border border-white/[0.05] bg-black/30 p-2.5 text-[11px] text-zinc-400 overflow-x-auto">
            {JSON.stringify(decision.checks, null, 2)}
          </pre>
        </details>
      )}
    </Card>
  );
}

function WouldSendCard({ wouldSend }) {
  if (!wouldSend) return null;
  const actionTone = {
    send: "border-emerald-400/30 bg-emerald-400/[0.06] text-emerald-100",
    deflect: "border-amber-400/30 bg-amber-400/[0.06] text-amber-100",
    propose_to_admin: "border-cyan-400/30 bg-cyan-400/[0.06] text-cyan-100",
  }[wouldSend.action] || "border-white/[0.08] text-zinc-200";
  return (
    <Card icon={MessageSquare} title="Would send" testid="rehearsal-card-would-send">
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[11px] ${actionTone}`}>
          {wouldSend.action}
        </span>
        <span className="text-zinc-500 text-[11px]">channel: {wouldSend.channel} · recipient: {wouldSend.recipient}</span>
      </div>
      <div className="mt-3 rounded-md border border-white/[0.06] bg-black/30 p-3 text-[12.5px] text-zinc-100 whitespace-pre-wrap break-words">
        {wouldSend.message || <span className="text-zinc-500 italic">(no message)</span>}
      </div>
    </Card>
  );
}

function WouldOpsDmCard({ rows, am }) {
  return (
    <Card icon={Inbox} title="Would ops_dm" testid="rehearsal-card-would-ops-dm">
      <div className="text-[11px] text-zinc-500 mb-2">
        {am?.user_id ? (
          <span>AM resolved: <span className="text-zinc-300">{am.email || am.user_id}</span></span>
        ) : (
          <span className="text-amber-200">AM unassigned — fallback to super_admins.</span>
        )}
      </div>
      {(rows || []).length === 0 ? (
        <div className="text-[12px] text-zinc-500 italic">No ops_dm would fire.</div>
      ) : (
        <ul className="space-y-2 text-[12.5px]">
          {rows.map((r, i) => (
            <li key={i} className="rounded-md border border-white/[0.06] bg-black/30 p-3">
              <div className="flex items-center justify-between gap-2">
                <span className="text-zinc-300">{r.recipient}</span>
                <span className="rounded border border-white/[0.08] px-1.5 py-0.5 text-[10.5px] text-zinc-400 font-mono">{r.category}</span>
              </div>
              <div className="mt-1 text-zinc-400 italic text-[12px]">{r.preview}</div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function KeyValue({ k, v }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">{k}</div>
      <div className="mt-0.5 text-zinc-200">{v ?? <span className="text-zinc-600">—</span>}</div>
    </div>
  );
}
function Mono({ children }) {
  return <span className="font-mono text-[12px]">{children}</span>;
}
function CheckMark({ tone = "emerald" }) {
  const cls = { emerald: "text-emerald-300", amber: "text-amber-300", rose: "text-rose-300" }[tone];
  return <CheckCircle2 className={`h-3.5 w-3.5 ${cls}`} />;
}
function Cross() {
  return <XCircle className="h-3.5 w-3.5 text-zinc-600" />;
}
